import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { userAuth, customerApi } from '../services/userAuth';
import { Button } from '../components/ui/button';
import { Loader2, Package, Calendar, MapPin, LogOut, Plus, ChevronRight, CheckCircle2, Clock, XCircle } from 'lucide-react';

const STATUS_STYLE = {
  pending: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', label: 'Pending Payment', icon: Clock },
  paid: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', label: 'Confirmed', icon: CheckCircle2 },
  confirmed: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', label: 'Confirmed', icon: CheckCircle2 },
  cancellation_requested: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', label: 'Cancellation Requested', icon: Clock },
  cancelled: { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200', label: 'Cancelled', icon: XCircle },
  completed: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', label: 'Completed', icon: CheckCircle2 },
};

function statusFor(r) {
  if (r.status === 'cancelled' || r.status === 'cancellation_requested' || r.status === 'completed') return r.status;
  if (r.payment_status === 'paid' || r.status === 'paid' || r.status === 'confirmed') return 'paid';
  return 'pending';
}

export default function Account() {
  const navigate = useNavigate();
  const user = userAuth.getUser();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    customerApi.myReservations()
      .then(setReservations)
      .catch((e) => setError(e.response?.data?.detail || 'Failed to load reservations'))
      .finally(() => setLoading(false));
  }, []);

  const handleLogout = () => {
    userAuth.logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="bg-gradient-to-b from-white to-slate-50 min-h-[calc(100vh-200px)]" data-testid="account-page">
      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#1a2e4e]" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Hi, {user?.first_name} <span className="text-[#dc2626]">👋</span>
            </h1>
            <p className="text-sm text-slate-500 mt-1">{user?.email}</p>
          </div>
          <button onClick={handleLogout} data-testid="account-logout"
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>

        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-[#1a2e4e]">Your Reservations</h2>
          <Link to="/contact" data-testid="account-new-reservation">
            <Button className="bg-[#dc2626] hover:bg-[#b91c1c] text-white">
              <Plus className="w-4 h-4 mr-1.5" /> New Reservation
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 text-[#1a2e4e] animate-spin" /></div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>
        ) : reservations.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center" data-testid="account-empty-state">
            <Package className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <p className="text-base font-medium text-slate-700">No reservations yet</p>
            <p className="text-sm text-slate-500 mt-1 mb-4">Create your first storage reservation to see it here.</p>
            <Link to="/contact">
              <Button className="bg-[#dc2626] hover:bg-[#b91c1c] text-white">Start a Reservation</Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-3" data-testid="reservations-list">
            {reservations.map(r => {
              const s = STATUS_STYLE[statusFor(r)] || STATUS_STYLE.pending;
              const Icon = s.icon;
              return (
                <Link key={r.id} to={`/account/reservations/${r.id}`} data-testid={`reservation-card-${r.id}`}
                  className="block bg-white border border-slate-200 rounded-2xl p-5 hover:border-[#1a2e4e]/40 hover:shadow-sm transition-all">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-mono text-xs text-slate-500">{r.reference_number}</span>
                        <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${s.bg} ${s.text} border ${s.border}`}>
                          <Icon className="w-3 h-3" /> {s.label}
                        </span>
                      </div>
                      <p className="text-base font-semibold text-[#1a2e4e]">{r.school_name}</p>
                      <div className="mt-2 grid sm:grid-cols-2 gap-y-1 gap-x-6 text-xs text-slate-600">
                        <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-slate-400" /> Pickup: <strong className="text-slate-800">{r.pickup_date}</strong></span>
                        <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-slate-400" /> {r.pickup_location}</span>
                        <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-slate-400" /> Delivery: <strong className="text-slate-800">{r.delivery_date}</strong></span>
                        <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-slate-400" /> {r.delivery_location}</span>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
